import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Package } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';

const AdminFormats = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFormat, setEditingFormat] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    size_cl: '',
    size_ml: '',
    price_multiplier: 1.0,
    min_order_quantity: 24,
    sort_order: 0,
    is_active: true
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadFormats();
  }, []);

  const loadFormats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/formats`);
      const data = await response.json();
      setFormats(data);
    } catch (error) {
      console.error('Error loading formats:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare i formati",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingFormat 
        ? `${backendUrl}/api/admin/formats/${editingFormat.id}`
        : `${backendUrl}/api/admin/formats`;
      
      const method = editingFormat ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({
          title: "Successo",
          description: editingFormat ? "Formato aggiornato" : "Formato creato"
        });
        loadFormats();
        resetForm();
      } else {
        throw new Error('Failed to save format');
      }
    } catch (error) {
      console.error('Error saving format:', error);
      toast({
        title: "Errore",
        description: "Impossibile salvare il formato",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (format) => {
    setEditingFormat(format);
    setFormData({
      name: format.name,
      size_cl: format.size_cl,
      size_ml: format.size_ml,
      price_multiplier: format.price_multiplier,
      min_order_quantity: format.min_order_quantity,
      sort_order: format.sort_order,
      is_active: format.is_active
    });
    setShowAddForm(true);
  };

  const handleDelete = async (formatId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo formato?')) return;

    try {
      const response = await fetch(`${backendUrl}/api/admin/formats/${formatId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        toast({
          title: "Successo",
          description: "Formato eliminato"
        });
        loadFormats();
      }
    } catch (error) {
      console.error('Error deleting format:', error);
      toast({
        title: "Errore",
        description: "Impossibile eliminare il formato",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      size_cl: '',
      size_ml: '',
      price_multiplier: 1.0,
      min_order_quantity: 24,
      sort_order: 0,
      is_active: true
    });
    setEditingFormat(null);
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Gestione Formati</h1>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuovo Formato
          </button>
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">Come funziona il prezzo:</h3>
          <p className="text-blue-800 text-sm">
            Il <strong>Price Multiplier</strong> moltiplica il prezzo base del liquore.
            <br />
            Es: Amaro base €10 × Formato 100cl (multiplier 1.8) = <strong>€18</strong>
            <br />
            Il formato 50cl dovrebbe avere multiplier 1.0 (riferimento base).
          </p>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editingFormat ? 'Modifica Formato' : 'Nuovo Formato'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Formato *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="es: 50cl"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dimensione (cl) *
                </label>
                <input
                  type="number"
                  value={formData.size_cl}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    size_cl: parseInt(e.target.value),
                    size_ml: parseInt(e.target.value) * 10
                  })}
                  placeholder="es: 50"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dimensione (ml) *
                </label>
                <input
                  type="number"
                  value={formData.size_ml}
                  onChange={(e) => setFormData({ ...formData, size_ml: parseInt(e.target.value) })}
                  placeholder="es: 500"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price Multiplier *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_multiplier}
                  onChange={(e) => setFormData({ ...formData, price_multiplier: parseFloat(e.target.value) })}
                  placeholder="es: 1.0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  1.0 = prezzo base, 1.8 = +80%, 0.7 = -30%
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordine Minimo *
                </label>
                <input
                  type="number"
                  value={formData.min_order_quantity}
                  onChange={(e) => setFormData({ ...formData, min_order_quantity: parseInt(e.target.value) })}
                  placeholder="es: 24"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordine Visualizzazione
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                  placeholder="es: 0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Formato attivo</span>
                </label>
              </div>

              <div className="md:col-span-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingFormat ? 'Salva Modifiche' : 'Crea Formato'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Formats List */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Formato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Dimensione
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Multiplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Ordine Minimo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Stato
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {formats.map((format) => (
                <tr key={format.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Package className="w-5 h-5 text-purple-600 mr-2" />
                      <span className="font-medium text-gray-900">{format.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {format.size_cl}cl ({format.size_ml}ml)
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {format.price_multiplier}x
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {format.min_order_quantity} pz
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      format.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {format.is_active ? 'Attivo' : 'Disattivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleEdit(format)}
                      className="text-purple-600 hover:text-purple-900 mr-3"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(format.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Example Pricing */}
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Esempio Calcolo Prezzi:</h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p>Liquore base: <strong>€10.00</strong></p>
            {formats.map((format) => (
              <p key={format.id}>
                • {format.name}: €10.00 × {format.price_multiplier} = <strong>€{(10 * format.price_multiplier).toFixed(2)}</strong>
              </p>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminFormats;
